/**
 * Auto-Tuning Meta-Layer — Staged Tournament (Successive Halving).
 *
 * Given the live dataset, finds a `SchedulerConfig` that performs best
 * for that dataset specifically. Strategy:
 *
 *   Phase 0  Dataset fingerprint — prune irrelevant dims, shape ranges,
 *            populate the overlay summary so the wait feels contextual.
 *   Phase 1  Seed population via Latin Hypercube over log-scaled ranges
 *            (+ pinned current, minimalist, rigid anchors).
 *   Phase 2  Cheap culling round (reduced attempts/iterations) — keep ~30%.
 *   Phase 3  Mid-fidelity round, 2 evaluations per candidate, median-ranked;
 *            top-3 also probed with single-weight ±1 step perturbations.
 *   Phase 4  Stability arena — full quality, 5 independent runs per survivor,
 *            ranked by  median − λ·IQR − μ·unfilled. Infeasible runs
 *            disqualify.
 *   Phase 5  Verify winner vs. pinned current settings on fresh independent
 *            runs. If no material improvement → refuse to recommend.
 *
 * The tuner never mutates engine/constraint code (per the plan) and never
 * writes the user's settings directly — it returns a recommendation the
 * user explicitly applies.
 */

import { DEFAULT_CONFIG, type Participant, type SchedulerConfig, type Task } from '../models/types';
import { buildScoreContextFields } from '../utils/capacity';
import {
  computeRankScore,
  iqr,
  latinHypercubeSample,
  maxUnfilledIn,
  median,
  mulberry32,
  UNFILLED_RANK_PENALTY,
} from '../utils/tuner-math';
import * as store from './config-store';
import { makeInlinePool, TunerCancelled, type TunerJob, type TunerPool, type TunerPoolEnv } from './tuner-concurrent';
import type { EvalResult, FrozenEnv } from './tuner-eval';
import { escHtml, getStoredDefaultAttempts } from './ui-helpers';
import { getWeightFieldLabel } from './weight-labels';

// ═══════════════════════════════════════════════════════════════════════════
//  Public types
// ═══════════════════════════════════════════════════════════════════════════

export interface TuneRecommendation {
  /** The suggested scheduler config (full, not a patch). */
  config: SchedulerConfig;
  /** Median composite score of the winner across Phase 5 seeds. */
  winnerMedian: number;
  /** IQR of the winner's composite score across Phase 5 seeds. */
  winnerIQR: number;
  /** Median composite score of the current settings across the same seeds. */
  baselineMedian: number;
  /** IQR of the current settings across Phase 5 seeds. */
  baselineIQR: number;
  /** Delta in median score: winner − baseline. Positive ⇒ winner is better. */
  medianDelta: number;
  /** True ⇒ we recommend applying; false ⇒ current settings are competitive. */
  recommend: boolean;
  /** Short Hebrew explanation of the decision. */
  reason: string;
  /** Fingerprint summary shown to the user. */
  fingerprintSummary: string;
  /** Per-dim diff: { key, baseline, tuned }. */
  diff: Array<{ key: keyof SchedulerConfig; baseline: number; tuned: number }>;
  /** Wall-clock duration of the run (ms). */
  durationMs: number;
}

// ═══════════════════════════════════════════════════════════════════════════
//  Runtime state
// ═══════════════════════════════════════════════════════════════════════════

type TunerPhase = 0 | 1 | 2 | 3 | 4 | 5 | -1; // -1 = done / idle

interface TuneState {
  phase: TunerPhase;
  /** Human-readable phase label (Hebrew). */
  phaseLabel: string;
  /** Total evaluations planned for current phase. */
  phaseTotal: number;
  /** Evaluations completed in current phase. */
  phaseDone: number;
  /** Fingerprint summary HTML line. */
  fingerprintSummary: string;
  /** Rolling best known median so far. */
  bestMedian: number | null;
  /** Rolling best known unfilled count. */
  bestUnfilled: number;
  /** Set true by `cancelAutoTune()`; evaluations check between candidates. */
  canceled: boolean;
  /** Start wall time. */
  startMs: number;
  /** Last phase for which the bracket DOM was rendered. Avoids innerHTML
   * churn on every eval — that would reset CSS animations. */
  lastRenderedPhase: TunerPhase | null;
  /** Baseline config (the pinned `current` candidate) and the active dims,
   * used to compute the live leader-vs-current diff preview. Pinned once per
   * run after Phase 2 identifies the `current` candidate; null until then. */
  leaderDiffBaseline: SchedulerConfig | null;
  leaderDiffDims: Dim[] | null;
  /** Human-readable diff of the leading candidate vs. current settings,
   * rendered below the metrics. Null until a non-`current` leader emerges. */
  leaderDiffLabel: string | null;
  /** Set true by `observeCandidate` when a new best is found. The next
   * `updateOverlay` consumes it to retrigger the score-pulse animation, then
   * clears it — preserving single-fire pulse semantics. */
  bestImproved: boolean;
}

let _state: TuneState | null = null;
let _isTuning = false;
/** The evaluation pool for the active run (Web Worker pool or inline fallback).
 *  cancelAutoTune() terminates it; runAutoTune()'s finally clears it. */
let _pool: TunerPool | null = null;
/** Injected by the web entry (app.ts) so the Worker-spawning pool stays out of
 *  the Node/persistence import graph. When null, the tuner uses the main-thread
 *  inline pool (tests / Worker-less runtimes). */
let _poolFactory: ((env: TunerPoolEnv) => Promise<TunerPool>) | null = null;

/** Wire the Web Worker pool factory. Called once at web startup (app.ts). */
export function setAutoTunerPoolFactory(fn: (env: TunerPoolEnv) => Promise<TunerPool>): void {
  _poolFactory = fn;
}

export function isAutoTuning(): boolean {
  return _isTuning;
}

export function cancelAutoTune(): void {
  if (!_state || _state.canceled) return;
  _state.canceled = true;
  // Terminate the pool so a long Phase-4/5 solve is killed immediately instead
  // of after up to ~minutes. Any in-flight runBatch rejects with TunerCancelled,
  // which runTournament turns into a `return null` (the run is discarded).
  _pool?.terminate();
  // Replace the phase label with an acknowledgement so the user sees the
  // cancel was accepted even while the current evaluation finishes running.
  _state.phaseLabel = 'מבטל… ההערכה הנוכחית תסתיים מיד';
  updateOverlay();
}

// ═══════════════════════════════════════════════════════════════════════════
//  Search space definition
// ═══════════════════════════════════════════════════════════════════════════

type TunedKey =
  | 'minRestWeight'
  | 'restPerGapWeight'
  | 'l0FairnessWeight'
  | 'seniorFairnessWeight'
  | 'lowPriorityLevelPenalty'
  | 'dailyBalanceWeight'
  | 'notWithPenalty'
  | 'taskNamePreferencePenalty'
  | 'taskNameAvoidancePenalty'
  | 'taskNamePreferenceBonus'
  | 'splitPenalty';

interface Dim {
  key: TunedKey;
  /** Inclusive min of the log-scaled sampling range. */
  min: number;
  /** Inclusive max of the log-scaled sampling range. */
  max: number;
  /** Sample and round as an integer? (Most fields are effectively integer in UX.) */
  integer: boolean;
}

const BASE_DIMS: Dim[] = [
  { key: 'minRestWeight', min: 1, max: 200, integer: true },
  // min must be ≥ 1: the LHS sampler is log-scaled, so min=0 → log(0) = -Infinity
  // collapses every sample to 0. The slider still permits 0 (manual disable);
  // the tuner explores [1, 50] which is the practically-useful active range.
  { key: 'restPerGapWeight', min: 1, max: 50, integer: true },
  { key: 'l0FairnessWeight', min: 1, max: 500, integer: true },
  { key: 'seniorFairnessWeight', min: 1, max: 200, integer: true },
  { key: 'lowPriorityLevelPenalty', min: 50, max: 20000, integer: true },
  { key: 'dailyBalanceWeight', min: 1, max: 500, integer: true },
  { key: 'notWithPenalty', min: 10, max: 20000, integer: true },
  { key: 'taskNamePreferencePenalty', min: 1, max: 1000, integer: true },
  { key: 'taskNameAvoidancePenalty', min: 1, max: 1000, integer: true },
  { key: 'taskNamePreferenceBonus', min: 1, max: 500, integer: true },
  // The in-run quality-split gate. Log-scaled like the other penalty dims.
  // Floor widened 2026-05-23 from 100 → 1 after the tuning sweep in
  // `src/bench-split-tuning` showed the productive band is {1..25}, an order
  // of magnitude below the old floor. Ceiling kept high so the tuner can
  // still suppress splitting on datasets where it doesn't help. Pruned when
  // nothing is splittable (see buildFingerprint).
  { key: 'splitPenalty', min: 1, max: 20000, integer: true },
];

// ═══════════════════════════════════════════════════════════════════════════
//  Phase 0 — dataset fingerprint
// ═══════════════════════════════════════════════════════════════════════════

interface Fingerprint {
  participantCount: number;
  levelCounts: Record<string, number>;
  notWithPairCount: number;
  preferenceCount: number; // participants with any task-name preference
  lowPrioritySlotCount: number;
  /** Occurrences whose frozen effective `splittable` flag is on (= a
   *  splittable template AND `splittingMode !== 'off'`). Zero ⇒ splitting
   *  can never happen this run ⇒ splitPenalty is pruned from the search. */
  splittableTaskCount: number;
  /** The dims we actually search over (pruned). */
  activeDims: Dim[];
  summaryHebrew: string;
}

function buildFingerprint(
  participants: Participant[],
  tasks: Task[],
  splittingMode: 'off' | 'feasibility' | 'quality',
): Fingerprint {
  const levelCounts: Record<string, number> = {};
  let notWithPairCount = 0;
  let preferenceCount = 0;
  const seenPairs = new Set<string>();

  // Reachable task names (by sourceName) — a preference for a name absent from
  // the schedule can never be satisfied, so it must NOT activate the SC-10
  // preference tuning dims / density shaping below (mirrors the scoring-side
  // reachability guard in soft-constraints' buildReachableTaskNames).
  const reachablePrefNames = new Set<string>();
  for (const t of tasks) if (t.sourceName) reachablePrefNames.add(t.sourceName);

  for (const p of participants) {
    levelCounts[`L${p.level}`] = (levelCounts[`L${p.level}`] ?? 0) + 1;
    if (
      (p.preferredTaskName && reachablePrefNames.has(p.preferredTaskName)) ||
      (p.lessPreferredTaskName && reachablePrefNames.has(p.lessPreferredTaskName))
    )
      preferenceCount++;
    for (const other of p.notWithIds ?? []) {
      const key = p.id < other ? `${p.id}|${other}` : `${other}|${p.id}`;
      if (!seenPairs.has(key)) {
        seenPairs.add(key);
        notWithPairCount++;
      }
    }
  }

  let lowPrioritySlotCount = 0;
  let splittableTaskCount = 0;
  for (const t of tasks) {
    for (const s of t.slots) {
      if (s.acceptableLevels.some((e) => e.lowPriority)) lowPrioritySlotCount++;
    }
    // `Task.splittable` is the frozen effective flag (template.splittable &&
    // splittingMode !== 'off'), stamped at generation — so this is 0 when
    // splitting is off OR no template is splittable.
    if (t.splittable) splittableTaskCount++;
  }

  const seniorCount = (levelCounts.L2 ?? 0) + (levelCounts.L3 ?? 0) + (levelCounts.L4 ?? 0);

  // Prune irrelevant dims: a dim whose penalty/bonus can never fire is held
  // constant at the current user value (we treat it as "not part of search").
  const activeDims: Dim[] = [];
  for (const d of BASE_DIMS) {
    if (d.key === 'notWithPenalty' && notWithPairCount === 0) continue;
    if (
      (d.key === 'taskNamePreferencePenalty' ||
        d.key === 'taskNameAvoidancePenalty' ||
        d.key === 'taskNamePreferenceBonus') &&
      preferenceCount === 0
    )
      continue;
    if (d.key === 'lowPriorityLevelPenalty' && lowPrioritySlotCount === 0) continue;
    if (d.key === 'seniorFairnessWeight' && seniorCount === 0) continue;
    // `splitPenalty` only has behavioral effect in `quality` mode, so prune it
    // outside that mode (each clause covers a distinct reason):
    //  • splittableTaskCount === 0 — nothing can split this run, so the penalty
    //    term is always 0. This is the whole story in `off` mode, where every
    //    `Task.splittable` is stamped false at generation.
    //  • splittingMode !== 'quality' — in `feasibility` mode `getEffectiveConfig`
    //    substitutes splitPenalty = 0 everywhere, so the dim is inert even when
    //    tasks ARE splittable. (`off` is also caught here, but the first clause
    //    already covers it.)
    if (d.key === 'splitPenalty' && (splittableTaskCount === 0 || splittingMode !== 'quality')) continue;

    // Shape ranges based on dataset density
    const shaped: Dim = { ...d };
    if (d.key === 'taskNamePreferencePenalty' || d.key === 'taskNameAvoidancePenalty') {
      const density = preferenceCount / Math.max(1, participants.length);
      if (density < 0.2) shaped.max = Math.max(d.min + 1, Math.round(d.max * 0.3));
    }
    activeDims.push(shaped);
  }

  const parts: string[] = [`${participants.length} משתתפים`];
  if (seniorCount > 0) parts.push(`${seniorCount} סגל`);
  if (notWithPairCount > 0) parts.push(`${notWithPairCount} זוגות "אי התאמה"`);
  if (preferenceCount > 0) parts.push(`${preferenceCount} העדפות משימה`);
  if (splittableTaskCount > 0) parts.push(`${splittableTaskCount} ניתנות לפיצול`);
  parts.push(`${tasks.length} משימות`);
  const summaryHebrew = parts.join(' · ');

  return {
    participantCount: participants.length,
    levelCounts,
    notWithPairCount,
    preferenceCount,
    lowPrioritySlotCount,
    splittableTaskCount,
    activeDims,
    summaryHebrew,
  };
}

// ═══════════════════════════════════════════════════════════════════════════
//  Evaluation — run a full multi-attempt scheduling with candidate weights
// ═══════════════════════════════════════════════════════════════════════════

export function freezeEnv(
  participants: Participant[],
  tasks: Task[],
  splittingMode: 'off' | 'feasibility' | 'quality' = 'quality',
): FrozenEnv {
  const dayStart = store.getDayStartHour();
  // Shares scheduler._buildScoreCtx's score-context primitive: capacity-
  // proportional SC-3/SC-8 targets need each participant's available hours over
  // the schedule's task span, plus the task/participant maps and notWith index.
  return {
    dayStart,
    disabledHC: store.getDisabledHCSet(),
    restRuleMap: store.buildRestRuleMap(),
    splittingMode,
    ...buildScoreContextFields(tasks, participants, dayStart),
  };
}

// ═══════════════════════════════════════════════════════════════════════════
//  Candidate = full SchedulerConfig + per-run score history
// ═══════════════════════════════════════════════════════════════════════════

interface Candidate {
  id: number;
  label: string; // CURRENT_LABEL | 'floor' | 'ceiling' | 'lhs-12' etc.
  config: SchedulerConfig;
  results: EvalResult[];
}

/**
 * Label of the pinned baseline candidate (the user's current settings). It is
 * created once and then always kept as a tracked competitor through every cull
 * phase. The single literal lives here so the creation site and the lookup site
 * cannot drift; a mismatch would otherwise surface as the explicit throw below.
 */
const CURRENT_LABEL = 'current';

function makeCandidateFromVector(
  id: number,
  label: string,
  baseConfig: SchedulerConfig,
  dims: Dim[],
  vector: number[],
): Candidate {
  const config: SchedulerConfig = { ...baseConfig };
  for (let i = 0; i < dims.length; i++) {
    (config as unknown as Record<string, number>)[dims[i].key] = vector[i];
  }
  return { id, label, config, results: [] };
}

// ═══════════════════════════════════════════════════════════════════════════
//  Aggregation — median / IQR / ranking   (pure helpers in utils/tuner-math)
// ═══════════════════════════════════════════════════════════════════════════

function maxUnfilled(results: EvalResult[]): number {
  return maxUnfilledIn(results);
}

/**
 * Stability-aware rank score (higher is better).
 * Infeasible seeds are penalised via μ; Phase 4 also applies a hard disqualify.
 * λ=0.5 / μ=UNFILLED_RANK_PENALTY — unfilled slots dominate any typical IQR gain.
 */
function rankScore(results: EvalResult[]): number {
  return computeRankScore(results, 0.5, UNFILLED_RANK_PENALTY);
}

// ═══════════════════════════════════════════════════════════════════════════
//  Overlay UI (surgical DOM updates — bypasses renderAll)
// ═══════════════════════════════════════════════════════════════════════════

/** Cached handles for the overlay's stable elements. Populated once in
 *  ensureOverlay (the sole creation site) and nulled in removeOverlay (the sole
 *  teardown site), so updateOverlay — the per-eval callback — avoids re-querying
 *  ~10 nodes on every result. The handles are safe to cache: innerHTML is built
 *  exactly once, and the only mid-run innerHTML churn (the bracket) swaps the
 *  bracket's *children*, never these nodes themselves. */
type OverlayEls = {
  fingerprint: HTMLElement | null;
  phaseLabel: HTMLElement | null;
  phaseFill: HTMLElement | null;
  phaseStatus: HTMLElement | null;
  bestScore: HTMLElement | null;
  unfilledMetric: HTMLElement | null;
  bestUnfilled: HTMLElement | null;
  elapsed: HTMLElement | null;
  leaderDiff: HTMLElement | null;
  bracket: HTMLElement | null;
};
let _els: OverlayEls | null = null;

function ensureOverlay(): void {
  let overlay = document.getElementById('tune-overlay');
  if (!overlay) {
    overlay = document.createElement('div');
    overlay.id = 'tune-overlay';
    overlay.className = 'optim-overlay';
    overlay.innerHTML = renderOverlayHtml();
    document.body.appendChild(overlay);
    overlay.addEventListener('click', (e) => {
      const btn = (e.target as HTMLElement).closest('[data-action="tune-cancel"]');
      if (btn) cancelAutoTune();
    });
  }
  // Cache the stable handles (scoped to the overlay root, so a stray same-id
  // node elsewhere in the document can't be picked up). Re-populate if the
  // node exists but the cache was lost (e.g. HMR), so updateOverlay never
  // silently no-ops against a live overlay.
  if (!_els) {
    _els = {
      fingerprint: overlay.querySelector('#tune-fingerprint'),
      phaseLabel: overlay.querySelector('#tune-phase-label'),
      phaseFill: overlay.querySelector('#tune-phase-fill'),
      phaseStatus: overlay.querySelector('#tune-phase-status'),
      bestScore: overlay.querySelector('#tune-best-score'),
      unfilledMetric: overlay.querySelector('#tune-unfilled-metric'),
      bestUnfilled: overlay.querySelector('#tune-best-unfilled'),
      elapsed: overlay.querySelector('#tune-elapsed'),
      leaderDiff: overlay.querySelector('#tune-leader-diff'),
      bracket: overlay.querySelector('#tune-bracket'),
    };
  }
}

function removeOverlay(): void {
  const overlay = document.getElementById('tune-overlay');
  overlay?.remove();
  _els = null;
}

function renderOverlayHtml(): string {
  return `
  <div class="optim-card tune-card tune-card-breathing" role="dialog" aria-modal="true" aria-label="כיול אוטומטי">
    <div class="tune-particles" aria-hidden="true">
      <span class="tune-bubble"></span>
      <span class="tune-bubble"></span>
      <span class="tune-bubble"></span>
      <span class="tune-bubble"></span>
      <span class="tune-bubble"></span>
      <span class="tune-bubble"></span>
    </div>
    <h3 id="tune-title">כיול הגדרות עבור הנתונים שלך…</h3>
    <div class="tune-fingerprint" id="tune-fingerprint"></div>
    <div class="tune-phase-label" id="tune-phase-label"></div>
    <div class="optim-progress-bar">
      <div class="optim-progress-fill" id="tune-phase-fill" style="width:0%"></div>
    </div>
    <div class="optim-status" id="tune-phase-status"></div>
    <div class="tune-bracket" id="tune-bracket"></div>
    <div class="optim-metrics tune-metrics" aria-live="polite" aria-atomic="false">
      <div class="optim-metric">
        <span class="optim-metric-label">הציון הטוב ביותר</span>
        <span class="optim-metric-value" id="tune-best-score">—</span>
      </div>
      <div class="optim-metric" id="tune-unfilled-metric" style="display:none">
        <span class="optim-metric-label">משבצות לא מאוישות</span>
        <span class="optim-metric-value" id="tune-best-unfilled">0</span>
      </div>
    </div>
    <div class="tune-leader-diff" id="tune-leader-diff" aria-live="polite"></div>
    <div class="tune-elapsed" id="tune-elapsed"></div>
    <div class="tune-actions">
      <button class="btn-sm btn-outline" data-action="tune-cancel">ביטול</button>
    </div>
  </div>`;
}

function updateOverlay(): void {
  if (!_state) return;
  ensureOverlay();
  const s = _state;
  const els = _els;
  if (!els) return;

  const fp = els.fingerprint;
  if (fp) fp.textContent = s.fingerprintSummary;

  const label = els.phaseLabel;
  if (label) label.textContent = s.phaseLabel;

  const fill = els.phaseFill;
  if (fill) {
    const pct = s.phaseTotal > 0 ? Math.round((s.phaseDone / s.phaseTotal) * 100) : 0;
    fill.style.width = `${pct}%`;
  }

  const status = els.phaseStatus;
  if (status) status.textContent = `${s.phaseDone} / ${s.phaseTotal}`;

  const best = els.bestScore;
  if (best) {
    best.textContent = s.bestMedian == null ? '—' : s.bestMedian.toFixed(1);
    // Retrigger the pulse animation only when a new best was actually found.
    // Remove + force-reflow + add is the standard way to restart a CSS keyframe.
    if (s.bestImproved) {
      best.classList.remove('tune-score-pulse');
      void best.offsetWidth;
      best.classList.add('tune-score-pulse');
      s.bestImproved = false;
    }
  }

  const unMetric = els.unfilledMetric;
  if (unMetric) {
    const show = s.bestMedian != null && s.bestUnfilled > 0;
    unMetric.style.display = show ? '' : 'none';
    if (show) {
      const un = els.bestUnfilled;
      if (un) {
        un.textContent = String(s.bestUnfilled);
        un.className = 'optim-metric-value optim-warn';
      }
    }
  }

  const elapsed = els.elapsed;
  if (elapsed) elapsed.textContent = formatElapsed(performance.now() - s.startMs);

  const diff = els.leaderDiff;
  if (diff) diff.textContent = s.leaderDiffLabel ?? '';

  // Rebuild the bracket only when the phase actually changes. Otherwise
  // innerHTML churn would restart the `tune-bracket-pulse` animation on
  // every eval (~1-3s), producing a visible stutter.
  if (s.lastRenderedPhase !== s.phase) {
    const bracket = els.bracket;
    if (bracket) bracket.innerHTML = renderBracketHtml(s);
    s.lastRenderedPhase = s.phase;
  }
}

/** Human-readable wall time since tuning started. Updated every overlay paint. */
function formatElapsed(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  if (totalSec < 60) return `חלפו ${totalSec} שנ'`;
  const minutes = Math.floor(totalSec / 60);
  const seconds = totalSec % 60;
  if (seconds === 0) return `חלפו ${minutes} דק'`;
  return `חלפו ${minutes}:${String(seconds).padStart(2, '0')} דק'`;
}

function renderBracketHtml(s: TuneState): string {
  const labels = ['סקירה', 'דגימה', 'ניפוי', 'סינון', 'יציבות', 'אימות'];
  const segs: string[] = [];
  for (let i = 0; i < labels.length; i++) {
    const active = s.phase === i;
    const done = s.phase === -1 || s.phase > i;
    const cls = ['tune-bracket-seg'];
    if (active) cls.push('tune-bracket-active');
    if (done) cls.push('tune-bracket-done');
    // Two stacked glyphs — a 1-based index and a checkmark. CSS crossfades
    // between them when `.tune-bracket-done` is applied, instead of a
    // text-content swap (which can't be animated). The bracket is rebuilt
    // only on phase change (see updateOverlay), so each phase transition
    // triggers a single fresh animation.
    segs.push(
      `<div class="${cls.join(' ')}">` +
        `<span class="tune-bracket-num">` +
        `<span class="tune-bracket-num-pending">${i + 1}</span>` +
        `<span class="tune-bracket-num-check" aria-hidden="true">✓</span>` +
        `</span>` +
        `<span class="tune-bracket-label">${escHtml(labels[i])}</span>` +
        `</div>`,
    );
  }
  return segs.join('');
}

// ═══════════════════════════════════════════════════════════════════════════
//  Orchestrator
// ═══════════════════════════════════════════════════════════════════════════

function newState(): TuneState {
  return {
    phase: 0,
    phaseLabel: 'סקירת הנתונים שלך',
    phaseTotal: 0,
    phaseDone: 0,
    fingerprintSummary: '',
    bestMedian: null,
    bestUnfilled: 0,
    canceled: false,
    startMs: performance.now(),
    lastRenderedPhase: null,
    leaderDiffBaseline: null,
    leaderDiffDims: null,
    leaderDiffLabel: null,
    bestImproved: false,
  };
}

function setPhase(phase: TunerPhase, label: string, total: number): void {
  if (!_state) return;
  _state.phase = phase;
  _state.phaseLabel = label;
  _state.phaseTotal = total;
  _state.phaseDone = 0;
  // Reset the peak-tracker so the "leading score" panel reflects only
  // candidates actually alive in the current phase — otherwise it would
  // keep displaying a number from an eliminated Phase 2/3 candidate.
  _state.bestMedian = null;
  _state.bestUnfilled = 0;
  _state.leaderDiffLabel = null;
}

function recordEval(_result: EvalResult): void {
  if (!_state) return;
  _state.phaseDone++;
}

/** Pick the weight with the largest log-space change vs. baseline — this
 * surfaces the dimension the tuner is leaning on hardest, not the one that
 * happens to have the largest absolute number. Returns null if nothing
 * differs. Reads the baseline/dims pinned on `_state` for this run. */
function computeLeaderDiffLabel(leader: SchedulerConfig): string | null {
  const baselineCfg = _state?.leaderDiffBaseline;
  const dims = _state?.leaderDiffDims;
  if (!baselineCfg || !dims) return null;
  let best: { key: string; baseline: number; tuned: number; mag: number } | null = null;
  for (const d of dims) {
    const baseline = (baselineCfg as unknown as Record<string, number>)[d.key];
    const tuned = (leader as unknown as Record<string, number>)[d.key];
    if (baseline === tuned) continue;
    const mag = Math.abs(Math.log(Math.max(1, tuned)) - Math.log(Math.max(1, baseline)));
    if (!best || mag > best.mag) best = { key: d.key, baseline, tuned, mag };
  }
  if (!best) return null;
  const label = getWeightFieldLabel(best.key);
  return `המוביל: ${label} ${best.baseline} → ${best.tuned}`;
}

function observeCandidate(c: Candidate): void {
  if (!_state) return;
  if (c.results.length === 0) return;
  const med = median(c.results.map((r) => r.refScore));
  const unf = maxUnfilled(c.results);
  if (_state.bestMedian == null || med > _state.bestMedian) {
    _state.bestMedian = med;
    _state.bestUnfilled = unf;
    _state.bestImproved = true;
    _state.leaderDiffLabel = computeLeaderDiffLabel(c.config);
  }
}

/**
 * Yield to the event loop so the overlay can repaint and click events fire.
 * Mirrors the pattern in `optimizeMultiAttemptAsync`.
 */
function yieldToUi(): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, 0));
}

export async function runAutoTune(): Promise<TuneRecommendation | null> {
  if (_isTuning) return null;
  _isTuning = true;
  _state = newState();

  const participants = store.getAllParticipants();
  if (participants.length === 0) {
    _isTuning = false;
    _state = null;
    throw new Error('אין משתתפים — הוסף משתתפים לפני כיול אוטומטי.');
  }

  // Resolve tasks via the app-layer template-expansion by letting the caller
  // provide them. To avoid circular imports with app.ts, we re-build the
  // engine exactly like doGenerate() does by reading the live store via a
  // thin helper the caller injects. See `setTaskFactory` below.
  if (!_taskFactory) {
    _isTuning = false;
    _state = null;
    throw new Error('auto-tuner: task factory not initialised — call setTaskFactory() at startup.');
  }
  const tasks = _taskFactory();
  if (tasks.length === 0) {
    _isTuning = false;
    _state = null;
    throw new Error('אין משימות — הוסף תבניות משימה לפני כיול אוטומטי.');
  }

  try {
    return await runTournament(participants, tasks);
  } finally {
    _isTuning = false;
    _state = null;
    try {
      _pool?.terminate();
    } catch {
      /* ignore */
    }
    _pool = null;
    removeOverlay();
  }
}

// Task factory injection — avoids circular import with app.ts.
let _taskFactory: (() => Task[]) | null = null;
export function setAutoTunerTaskFactory(fn: () => Task[]): void {
  _taskFactory = fn;
}

// ═══════════════════════════════════════════════════════════════════════════
//  Tournament body
// ═══════════════════════════════════════════════════════════════════════════

/** Baseline attempts count used for Phase 4/5 (full quality). */
function getBaselineAttempts(): number {
  // getStoredDefaultAttempts() already guarantees a finite integer ≥ 1
  // (FALLBACK_DEFAULT_ATTEMPTS = 60), so no extra guard/fallback is needed.
  return getStoredDefaultAttempts();
}

async function runTournament(participants: Participant[], tasks: Task[]): Promise<TuneRecommendation | null> {
  if (!_state) throw new Error('auto-tuner: internal state missing (should not happen).');
  const s = _state;

  // ── Phase 0 ────────────────────────────────────────────────────────────
  setPhase(0, 'סקירת הנתונים שלך', 1);
  const splittingMode = store.getAlgorithmSettings().splittingMode ?? 'quality';
  const fp = buildFingerprint(participants, tasks, splittingMode);
  s.fingerprintSummary = fp.summaryHebrew;
  ensureOverlay();
  updateOverlay();
  await yieldToUi();
  s.phaseDone = 1;
  if (s.canceled) return null;

  // Freeze engine inputs once so every evaluation in this run sees the same
  // world — participants/tasks were already captured in runAutoTune(), and
  // this extends the freeze to disabledHC/dayStart/restRuleMap.
  const frozen = freezeEnv(participants, tasks, splittingMode);

  const baseAttempts = getBaselineAttempts();
  const baseIter = DEFAULT_CONFIG.maxIterations;
  const mkJob = (config: SchedulerConfig, attempts: number, maxIterations: number): TunerJob => {
    // Legacy per-eval SA wall-clock cap, byte-identical to the pre-pool serial
    // tuner: scale the candidate config's cap by attempts/baseAttempts so cheap
    // phases get a tighter time budget. Computed here (main thread) and carried
    // on the job so the worker reproduces the exact same budget.
    const solverRatio = Math.max(0.1, Math.min(1, attempts / Math.max(1, baseAttempts)));
    return {
      config,
      attempts,
      maxIterations,
      maxSolverTimeMs: Math.max(1500, Math.round(config.maxSolverTimeMs * solverRatio)),
    };
  };

  // Spin up the evaluation pool: Web Workers in the browser (injected factory),
  // or the main-thread inline pool as a fallback (tests / Worker-less runtimes).
  // The frozen world is sent to the workers ONCE; jobs carry only the per-eval
  // delta (candidate config + phase budget).
  const env: TunerPoolEnv = { participants, tasks, frozen };
  let pool: TunerPool;
  try {
    pool = _poolFactory ? await _poolFactory(env) : makeInlinePool(env);
  } catch (err) {
    // The Web Worker pool failed to spawn (e.g. a CSP that blocks blob: workers,
    // a chunk-load failure offline, or memory pressure). Degrade to the
    // main-thread inline pool rather than failing the whole tune.
    console.warn('[AutoTuner] Web Worker pool unavailable — falling back to main thread.', err);
    pool = makeInlinePool(env);
  }
  _pool = pool;

  // Run one eval per entry through the pool, pushing each result onto its
  // candidate and updating the overlay as results land (out of order). Returns
  // false if the run was canceled (terminate() ⇒ TunerCancelled), so callers
  // bail to null. The cull/ranking after a batch sees the FULL result set.
  const runJobs = async (
    entries: Array<{ cand: Candidate; attempts: number; maxIterations: number }>,
  ): Promise<boolean> => {
    const jobs = entries.map((e) => mkJob(e.cand.config, e.attempts, e.maxIterations));
    try {
      await pool.runBatch(jobs, (_job, res, i) => {
        const cand = entries[i].cand;
        cand.results.push(res);
        recordEval(res);
        observeCandidate(cand);
        updateOverlay();
      });
    } catch (e) {
      if (e instanceof TunerCancelled) return false;
      throw e;
    }
    return !s.canceled;
  };

  // Phase-5 variant: collect raw results into a local array (not onto a
  // candidate). Returns null if canceled.
  const runRawJobs = async (jobs: TunerJob[]): Promise<EvalResult[] | null> => {
    try {
      return await pool.runBatch(jobs, (_job, res) => {
        recordEval(res);
        updateOverlay();
      });
    } catch (e) {
      if (e instanceof TunerCancelled) return null;
      throw e;
    }
  };

  // Per-phase cost profile (attempts × maxIterations). The baselines below
  // target a ~10-minute budget on a mid-size dataset; real wall time is
  // governed by the scheduler itself and surfaced live via the ETA panel.
  const phase2Attempts = Math.max(8, Math.round(baseAttempts * 0.15));
  const phase2Iter = Math.max(5000, Math.round(baseIter * 0.3));
  const phase3Attempts = Math.max(20, Math.round(baseAttempts * 0.4));
  const phase3Iter = Math.max(15000, Math.round(baseIter * 0.6));
  const phase4Attempts = Math.max(40, Math.round(baseAttempts * 0.8));
  const phase4Iter = baseIter;
  const phase5Attempts = baseAttempts;
  const phase5Iter = baseIter;

  // ── Phase 1 — sample population ────────────────────────────────────────
  setPhase(1, 'דגימת מועמדים', 1);
  const currentSettings = store.getAlgorithmSettings();
  const rng = mulberry32(Date.now() | 0);
  const lhsCount = 40;
  const vectors = latinHypercubeSample(fp.activeDims, lhsCount, rng);

  const candidates: Candidate[] = [];
  let cid = 0;

  // Pinned current settings
  candidates.push({ id: cid++, label: CURRENT_LABEL, config: { ...currentSettings.config }, results: [] });

  // Minimalist & rigid anchors
  const floorVec = fp.activeDims.map((d) => d.min);
  const ceilingVec = fp.activeDims.map((d) => d.max);
  candidates.push(
    makeCandidateFromVector(cid++, 'floor', currentSettings.config, fp.activeDims, floorVec),
    makeCandidateFromVector(cid++, 'ceiling', currentSettings.config, fp.activeDims, ceilingVec),
  );

  for (let i = 0; i < vectors.length; i++) {
    candidates.push(makeCandidateFromVector(cid++, `lhs-${i}`, currentSettings.config, fp.activeDims, vectors[i]));
  }

  const perturbDimCount = Math.min(3, fp.activeDims.length);
  s.phaseDone = 1;
  updateOverlay();
  if (s.canceled) return null;

  // ── Phase 2 — cheap culling ────────────────────────────────────────────
  setPhase(2, 'ניפוי מהיר', candidates.length);
  updateOverlay();
  if (!(await runJobs(candidates.map((c) => ({ cand: c, attempts: phase2Attempts, maxIterations: phase2Iter }))))) {
    return null;
  }

  // Keep top 30% (minimum 8, maximum 15). Ranking by single cheap score.
  const cheapSurvivors = sortByDesc(candidates, cheapRank).slice(
    0,
    Math.min(15, Math.max(8, Math.round(candidates.length * 0.3))),
  );

  // Always ensure current-settings candidate survives to Phase 3+.
  const currentCand = candidates.find((c) => c.label === CURRENT_LABEL);
  if (!currentCand) throw new Error('auto-tuner: missing pinned current candidate.');
  if (!cheapSurvivors.includes(currentCand)) cheapSurvivors.push(currentCand);

  // Pin the baseline for the live leader-diff preview now that we know the
  // `current` candidate's config. Active dims are the only ones the tuner
  // actually varies — comparing inactive dims would always be a no-op.
  s.leaderDiffBaseline = currentCand.config;
  s.leaderDiffDims = fp.activeDims;

  // ── Phase 3 — mid-fidelity + local perturbation probing ────────────────
  // Each survivor runs twice; then top-3 are perturbed on up to 3 dims.
  // The phaseTotal upper-bounds the perturbation count so the progress bar
  // never overshoots 100% when some perturbations are skipped (identical to
  // base after rounding / clamping).
  const phase3SurvivorEvals = cheapSurvivors.length * 2;
  const phase3PerturbUpper = Math.min(3, cheapSurvivors.length) * perturbDimCount;
  setPhase(3, 'סינון מעמיק', phase3SurvivorEvals + phase3PerturbUpper);
  updateOverlay();

  // Drop the single cheap Phase 2 result so the Phase 3 median is computed from
  // mid-fidelity evaluations only, then run 2 evals per survivor.
  for (const c of cheapSurvivors) c.results = [];
  const phase3Entries = cheapSurvivors.flatMap((c) => [
    { cand: c, attempts: phase3Attempts, maxIterations: phase3Iter },
    { cand: c, attempts: phase3Attempts, maxIterations: phase3Iter },
  ]);
  if (!(await runJobs(phase3Entries))) return null;

  // Perturb top-3 on up to 3 dims (halve/double one dim at a time).
  const rankedMid = sortByDesc(cheapSurvivors, midRank);
  const topThree = rankedMid.slice(0, 3);
  const perturbDims = fp.activeDims.slice(0, perturbDimCount);
  const perturbCandidates: Candidate[] = [];
  for (const seed of topThree) {
    for (const dim of perturbDims) {
      const base = seed.config[dim.key] ?? DEFAULT_CONFIG[dim.key];
      // Use the seeded RNG (not Math.random) so the tuner's stochastic
      // choices stay reproducible per-run given the same start seed.
      const perturbedVal = Math.min(dim.max, Math.max(dim.min, Math.round(base * (rng() < 0.5 ? 0.5 : 2))));
      if (perturbedVal === base) continue;
      perturbCandidates.push({
        id: cid++,
        label: `perturb-${seed.id}-${dim.key}`,
        config: { ...seed.config, [dim.key]: perturbedVal },
        results: [],
      });
    }
  }
  if (perturbCandidates.length > 0) {
    const ok = await runJobs(
      perturbCandidates.map((pc) => ({ cand: pc, attempts: phase3Attempts, maxIterations: phase3Iter })),
    );
    if (!ok) return null;
  }

  // Combine survivors + perturbation candidates, pick top-4 by mid rank.
  // Then ensure the pinned current-settings candidate is always a finalist
  // — but only evict the *bottom* of the finalist list, never a better one.
  const combined = [...cheapSurvivors, ...perturbCandidates];
  let phase4Survivors = sortByDesc(combined, midRank).slice(0, 4);
  if (!phase4Survivors.includes(currentCand)) {
    if (phase4Survivors.length >= 4) phase4Survivors = phase4Survivors.slice(0, 3);
    phase4Survivors.push(currentCand);
  }

  // ── Phase 4 — stability arena ──────────────────────────────────────────
  const phase4EvalCount = phase4Survivors.length * 5;
  setPhase(4, 'בדיקת יציבות', phase4EvalCount);
  updateOverlay();

  // Reset results so Phase 4 score isn't mixed with cheap-stage results, then
  // run 5 independent evals per survivor.
  for (const c of phase4Survivors) c.results = [];
  const phase4Entries = phase4Survivors.flatMap((c) =>
    Array.from({ length: 5 }, () => ({ cand: c, attempts: phase4Attempts, maxIterations: phase4Iter })),
  );
  if (!(await runJobs(phase4Entries))) return null;

  // Apply the feasibility gate: any candidate with ≥1 infeasible run is
  // disqualified from being chosen as the winner.
  const feasibleFinalists = phase4Survivors.filter((c) => !c.results.some((r) => r.unfilled > 0));
  const ranked = sortByDesc(feasibleFinalists, (c) => rankScore(c.results));
  const winner = ranked[0];
  // Distinguish "current was already best" from "nothing survived feasibility".
  const noFeasibleChallenger = feasibleFinalists.length === 0;

  // ── Phase 5 — verify winner vs. baseline on fresh seeds ────────────────
  // Total evals depends on the branch: no-challenger does only 3 (baseline
  // confirmation), winner path does 6 (winner+baseline × 3). Set the real
  // budget so the progress bar can actually reach 100%.
  const hasChallenger = winner != null && winner !== currentCand;
  const phase5Total = hasChallenger ? 6 : 3;
  setPhase(5, 'אימות מול הנוכחי', phase5Total);
  updateOverlay();

  let verdict: TuneRecommendation;
  if (winner == null || winner === currentCand) {
    // No challenger beat the baseline (either current was already best, or
    // no candidate passed the feasibility gate). Run a short confirmation on
    // the current settings alone so the user sees a stability estimate.
    const verifyResults = await runRawJobs(
      Array.from({ length: 3 }, () => mkJob(currentCand.config, phase5Attempts, phase5Iter)),
    );
    if (verifyResults === null || s.canceled) return null;
    const med = median(verifyResults.map((r) => r.refScore));
    const band = iqr(verifyResults.map((r) => r.refScore));
    verdict = {
      config: { ...currentCand.config },
      winnerMedian: med,
      winnerIQR: band,
      baselineMedian: med,
      baselineIQR: band,
      medianDelta: 0,
      recommend: false,
      reason: noFeasibleChallenger
        ? 'אף מועמד מתחרה לא עבר את בדיקת ההיתכנות — נשמרות ההגדרות הנוכחיות.'
        : 'ההגדרות הנוכחיות תחרותיות — אין צורך בשינוי.',
      fingerprintSummary: fp.summaryHebrew,
      diff: [],
      durationMs: Math.round(performance.now() - s.startMs),
    };
  } else {
    // Run winner + current over 3 evaluations each. All independent — the pool
    // runs them concurrently; the old main-thread "alternating" interleave was a
    // fairness device that is no longer needed.
    const phase5Jobs: TunerJob[] = [
      ...Array.from({ length: 3 }, () => mkJob(winner.config, phase5Attempts, phase5Iter)),
      ...Array.from({ length: 3 }, () => mkJob(currentCand.config, phase5Attempts, phase5Iter)),
    ];
    const phase5Results = await runRawJobs(phase5Jobs);
    if (phase5Results === null || s.canceled) return null;
    const winnerResults = phase5Results.slice(0, 3);
    const baselineResults = phase5Results.slice(3, 6);

    const wMed = median(winnerResults.map((r) => r.refScore));
    const wIQR = iqr(winnerResults.map((r) => r.refScore));
    const bMed = median(baselineResults.map((r) => r.refScore));
    const bIQR = iqr(baselineResults.map((r) => r.refScore));
    const anyWinnerInfeasible = winnerResults.some((r) => r.unfilled > 0);
    const delta = wMed - bMed;

    // Material-improvement threshold: must beat by ≥1% of |baseline| and at
    // least 5 points absolute, and must not widen IQR by more than 2×.
    const absThreshold = Math.max(5, Math.abs(bMed) * 0.01);
    const stabilityOk = wIQR <= Math.max(1, bIQR * 2);
    const recommend = !anyWinnerInfeasible && delta >= absThreshold && stabilityOk;

    const diff: TuneRecommendation['diff'] = [];
    for (const d of fp.activeDims) {
      const baseline = currentCand.config[d.key];
      const tuned = winner.config[d.key];
      if (baseline !== tuned) diff.push({ key: d.key, baseline, tuned });
    }

    verdict = {
      config: { ...winner.config },
      winnerMedian: wMed,
      winnerIQR: wIQR,
      baselineMedian: bMed,
      baselineIQR: bIQR,
      medianDelta: delta,
      recommend,
      reason: recommend
        ? `שיפור של ${delta.toFixed(1)} נקודות בציון החציוני (יציבות נשמרה).`
        : anyWinnerInfeasible
          ? 'המנצח הפיק ריצה אחת או יותר לא-תקינה באימות — לא מומלץ ליישם.'
          : `השיפור (${delta.toFixed(1)}) נמוך מסף המינימום או שהיציבות נפגעה — לא מומלץ ליישם.`,
      fingerprintSummary: fp.summaryHebrew,
      diff,
      durationMs: Math.round(performance.now() - s.startMs),
    };
  }

  s.phase = -1;
  updateOverlay();
  return verdict;
}

// ═══════════════════════════════════════════════════════════════════════════
//  Ranking helpers
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Decorate-sort-undecorate by a descending numeric key. Computes `keyOf(item)`
 * exactly ONCE per element, then sorts the cached keys — vs. calling it the
 * ~2·n·log₂n times a `(a, b) => keyOf(b) - keyOf(a)` comparator would (each
 * rank call re-runs median/IQR over the candidate's results). Returns a fresh
 * array, so the input is never mutated (the callers rely on that). The sort is
 * stable, so equal keys preserve input order — byte-identical ordering to the
 * comparator form it replaces.
 */
function sortByDesc<T>(arr: readonly T[], keyOf: (item: T) => number): T[] {
  return arr
    .map((item) => ({ item, key: keyOf(item) }))
    .sort((a, b) => b.key - a.key)
    .map((d) => d.item);
}

/** Single-eval cheap rank: feasibility-gated, then composite score. */
function cheapRank(c: Candidate): number {
  if (c.results.length === 0) return -Infinity;
  // Phase 2 runs ONE cheap eval per candidate; rank on that single result only
  // (λ=0 ⇒ no stability term — IQR is meaningless for one sample). Routed
  // through the shared scorer so the unfilled-penalty magnitude lives in one
  // place; the 1-element slice preserves the "first eval only" semantics.
  return computeRankScore([c.results[0]], 0, UNFILLED_RANK_PENALTY);
}

/** Mid-fidelity rank: median of evaluations minus unfilled penalty. */
function midRank(c: Candidate): number {
  // median(refScore) − μ·maxUnfilled with λ=0 (the IQR stability term is
  // deferred to Phase 4's rankScore). Empty results ⇒ −Infinity, handled
  // inside computeRankScore — same guard as before.
  return computeRankScore(c.results, 0, UNFILLED_RANK_PENALTY);
}
